import React from 'react';
import { Button, DataGrid, RotatingIcon, FlexRow, IconType } from 'ui-toolkit';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';
import { Show } from '../../../shared/Show';
import { DisplayPartyPurpose, ePartyPurpose } from '../../../../ui-toolkit/enums/ePartyPurpose';

export interface IAffidavitsTableProps {
  onGenerate: (party: ePartyPurpose) => void;
  onGenerateViaZdocs: (party: ePartyPurpose) => void;
  availablePartyPurpose: ePartyPurpose[];
  fetchingGeneration: Record<ePartyPurpose, boolean>;
  generatingParty: ePartyPurpose | null;
}

interface IColumnType {
  label: string;
  value: ePartyPurpose;
  fetching: boolean;
}

export const AffidavitsTable: React.FC<IAffidavitsTableProps> = ({
  onGenerate,
  onGenerateViaZdocs,
  availablePartyPurpose,
  fetchingGeneration,
  generatingParty,
}) => {
  const isZDocPackageEnabled = useHasFeatureFlag('zdoc.affidavit.packages');
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const getButtonText = (party: ePartyPurpose): string => {
    if (isZillowClosingEnabled) {
      // Determines party name for button text: Buyer, Seller, or Borrower (default for refinance)
      const partyName = party === ePartyPurpose.buyer ? 'Buyer' : party === ePartyPurpose.seller ? 'Seller' : 'Borrower';
      return `Generate ZC ${partyName} Package`;
    }
    return 'Generate Affidavit Via ZDocs';
  };

  const data: IColumnType[] = availablePartyPurpose.map((type) => ({
    value: type,
    zdocValue: type,
    label: `${DisplayPartyPurpose[type]} Affidavit`,
    fetching: fetchingGeneration[type],
  }));

  const columns: IBootstrapTableColumnProps<IColumnType>[] = [
    {
      text: 'Affidavit Type',
      dataField: 'label',
      classes: (cell, row) => `label-${row.value}`,
    },
  ];

  // Show legacy Action column in legacy mode
  const isLegacyMode = !isZillowClosingEnabled;
  if (isLegacyMode) {
    columns.push({
      text: 'Action',
      dataField: 'value',
      classes: (cell, row) => `value-${row.value}`,
      formatter: (cell, row) => (
        <FlexRow>
          <Button intent={'ghost'} disabled={row.fetching} onClick={() => onGenerate(cell as ePartyPurpose)}>
            Generate Affidavit
          </Button>
          {row.fetching && <RotatingIcon type={IconType.Loading} />}
        </FlexRow>
      ),
    });
  }

  if (isZDocPackageEnabled) {
    columns.push({
      text: isZillowClosingEnabled ? 'Action' : 'ZDoc Action',
      dataField: 'zdocValue',
      classes: (cell, row) => `value-${row.value}`,
      formatter: (cell, row) => {
        // Show ZDoc button for buyer (always when feature is enabled)
        // Show ZDoc button for seller and borrower only when Zillow Closing is enabled
        const shouldShowButton = 
          row.value === ePartyPurpose.buyer || 
          (row.value === ePartyPurpose.seller && isZillowClosingEnabled) ||
          (row.value === ePartyPurpose.borrower && isZillowClosingEnabled);
        
        return (
          <Show when={shouldShowButton}>
            <FlexRow>
              <Button 
                intent={'ghost'} 
                disabled={generatingParty === row.value} 
                onClick={() => onGenerateViaZdocs(row.value as ePartyPurpose)}
              >
                {getButtonText(row.value as ePartyPurpose)}
              </Button>
              {row.fetching && <RotatingIcon type={IconType.Loading} />}
            </FlexRow>
          </Show>
        );
      },
    });
  }
  return <DataGrid columns={columns} keyField={'value'} data={data} />;
};
